import { useRef, useState } from 'react';
import {
  MutationStatus,
  UseMutateAsyncFunction,
  useMutation
} from 'react-query';

import { getAPIErrorMessage } from '@nw/shared/utils';

import { OrderStatusEnum } from '../../../constants/PaymentConstants';
import {
  GetOrderStatusRequestType,
  GetOrderStatusResponseType
} from '../../../services/PaymentServices/APIService/types';

import usePaymentServices from '../usePaymentServices';

interface UseGetOrderStatusAPIInterface {
  getOrderStatusApi: UseMutateAsyncFunction<
    GetOrderStatusResponseType,
    unknown,
    {
      orderId: string;
    },
    unknown
  >;
  error: string;
  status: MutationStatus;
  data: any;
  response: any;
  cancelGetOrderStatus: () => void;
}

const useGetOrderStatus = (): UseGetOrderStatusAPIInterface => {
  const { paymentServices } = usePaymentServices();
  const [response, setResponse] = useState(null);
  const cancelGetOrderAPIRef = useRef(false);

  const getOrderStatus = ({ orderId }) => {
    const request: GetOrderStatusRequestType = {
      order_id: orderId
    };
    return paymentServices.getOrderStatus(request).then((response) => {
      setResponse(response);
      if (response.order_status === OrderStatusEnum.ACTIVE) {
        return response;
      } else {
        throw response;
      }
    });
  };

  const cancelGetOrderStatus = () => {
    cancelGetOrderAPIRef.current = true;
  };

  const { mutateAsync, error, status, data } = useMutation(getOrderStatus, {
    mutationKey: ['getOrderStatusApi'],
    retry: (failureCount: number, error) => {
      return !cancelGetOrderAPIRef.current && failureCount < 15;
    },
    retryDelay: (attempt) =>
      Math.min(attempt > 0 ? 2 ** attempt * 1000 : 1000, 10 * 3000)
  });

  const getOrderStatusApi = mutateAsync;

  return {
    getOrderStatusApi,
    error: error ? getAPIErrorMessage(error) : '',
    status,
    data,
    response,
    cancelGetOrderStatus
  };
};

export { useGetOrderStatus };
